import React from 'react';
import {
  Alert, Button, ConfirmDialog, Dialog, DialogBody, DialogContent, DialogFooter,
  DialogHeader, DialogTitle, Spinner,
} from '@parsina/framework';

import { useTranslation } from '../i18n';
import { useShortcut } from '../lib/shortcuts';

/**
 * The scaffold every task dialog in this panel is built on.
 *
 * It exists because the four dialogs it replaces each got the SAME three things
 * wrong, in the same way, and a shared scaffold is the only way that stops
 * happening again:
 *
 *   1. THEY CLOSED BEFORE THE MUTATION RESOLVED. `setOpen(false)` ran, then the
 *      request went out. On failure the operator's typed suspension reason or
 *      activation note was already gone, so a transient 503 cost them the text
 *      they had composed — and DESIGN-CONCEPT §6.4 calls clearing a form on a
 *      failed submit a review-blocking defect. Here the dialog stays open, keeps
 *      every keystroke, and shows the reason inline.
 *   2. THERE WAS NO PENDING STATE. The submit button stayed enabled for the whole
 *      round trip, so a double click sent two activations — two audited writes,
 *      two billing periods. Double-submit is impossible by construction now: the
 *      button is disabled and the whole body is inert while `pending`.
 *   3. A DIRTY FORM CLOSED SILENTLY on Esc or an overlay click. §5.2 says an
 *      overlay with unsaved input requires the explicit ✕/انصراف and asks first.
 *
 * `Ctrl+Enter` submits, registered through the shortcut registry rather than a
 * local key listener, so it appears in the `?` overlay and hands the key back on
 * unmount (clinic-app/CLAUDE.md §4.2).
 *
 * Props:
 *   open, onOpenChange, title
 *   lead          node above the fields — a consequence `Alert`, usually
 *   error         string | null → an inline error `Alert` at the top of the body
 *   pending       the mutation is in flight
 *   dirty         the operator has typed something not yet submitted
 *   submitLabel, submitVariant, submitDisabled, onSubmit
 *   footerNote    quiet text beside the buttons (a helper, a hint)
 *   size          the kit Dialog's size (sm | md | lg | xl); unset = the kit's own
 *   layout        'scroll' (default: the whole box scrolls) | 'frame' (a fixed
 *                 header, a scrolling body and a docked footer — for a long form
 *                 whose submit must never scroll out of reach, DESIGN_CONCEPT §5.2)
 */
export default function TaskDialog({
  open,
  onOpenChange,
  title,
  lead,
  error,
  pending = false,
  dirty = false,
  submitLabel,
  submitVariant = 'default',
  submitDisabled = false,
  onSubmit,
  footerNote,
  size,
  layout = 'scroll',
  children,
  'data-testid': testId,
}) {
  const { t } = useTranslation();
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);

  const canSubmit = !pending && !submitDisabled;

  useShortcut('ctrl+enter', () => { if (open && canSubmit) onSubmit(); }, {
    label: t('shortcuts.action.submit'),
    enabled: open && canSubmit,
  });

  /**
   * Every route out of the dialog funnels through here.
   *
   * While pending, nothing closes it: a request is in flight and closing the
   * dialog would leave the operator unsure whether the write landed. Dirty and
   * idle asks first. Clean and idle just closes.
   */
  const requestClose = (next) => {
    if (next) { onOpenChange(true); return; }
    if (pending) return;
    if (dirty) { setConfirmDiscard(true); return; }
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={requestClose}>
        <DialogContent
          size={size}
          layout={layout}
          showClose={!pending}
          onEscapeKeyDown={(e) => { if (pending || dirty) e.preventDefault(); if (dirty && !pending) setConfirmDiscard(true); }}
          onPointerDownOutside={(e) => { if (pending || dirty) e.preventDefault(); if (dirty && !pending) setConfirmDiscard(true); }}
          data-testid={testId}
        >
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>

          <DialogBody className="flex flex-col gap-3">
            {/* A failure the operator can read, where they are looking — not a
                toast that vanishes while they are re-reading what they typed. */}
            {error ? (
              <Alert variant="error" data-testid={testId ? `${testId}-error` : 'dialog-error'}>
                {error}
              </Alert>
            ) : null}
            {lead}
            {children}
          </DialogBody>

          <DialogFooter className={layout === 'frame' ? 'items-center justify-start gap-2' : 'mt-6 items-center justify-start gap-2'}>
            <Button
              variant={submitVariant}
              disabled={!canSubmit}
              onClick={onSubmit}
              aria-busy={pending || undefined}
              data-testid={testId ? `${testId}-submit` : undefined}
            >
              {pending ? <Spinner size={16} thickness={2} color="currentColor" /> : null}
              {submitLabel}
            </Button>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => requestClose(false)}
              data-testid={testId ? `${testId}-cancel` : undefined}
            >
              {t('common.actions.cancel')}
            </Button>
            {footerNote ? (
              <span className="ms-1 text-xs text-muted-foreground">{footerNote}</span>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stacked above the dialog it guards — the one overlay stack §5.2 allows. */}
      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title={t('clinic.dialogs.dirty.title')}
        description={t('clinic.dialogs.dirty.body')}
        confirmLabel={t('clinic.dialogs.dirty.confirm')}
        cancelLabel={t('clinic.dialogs.dirty.cancel')}
        destructive
        onConfirm={() => { setConfirmDiscard(false); onOpenChange(false); }}
        data-testid={testId ? `${testId}-discard` : undefined}
      />
    </>
  );
}
